import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import StatsCards from "../components/StatsCards";
import EmptyState from "../components/EmptyState";
import GamesList from "../components/GamesList";
import CardBasedMobilePicks from "../components/CardBasedMobilePicks";
import { useIsMobile } from "../hooks/useIsMobile";
import { apiService } from "../utils/apiService";
import { useLoadingState } from "../hooks/useLoadingState";

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcomingGames, setUpcomingGames] = useState([]);
  const [userPicks, setUserPicks] = useState({});
  const isMobile = useIsMobile();
  const { loading, withLoading } = useLoadingState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      await withLoading(async () => {
        // Fetch user stats
        const statsResponse = await apiService.get("/users/stats");

        // Fetch upcoming games (next 48 hours)
        const gamesResponse = await apiService.get(
          "/games/upcoming?hours=48&includeLines=true&limit=16",
        );

        // Fetch user's picks for these games
        if (gamesResponse.length > 0) {
          const gameIds = gamesResponse.map((game) => game.id);
          const picksResponse = await apiService.get(
            `/picks/games?gameIds=${gameIds.join(",")}`,
          );

          // Convert picks array to object with gameId as key
          const picksMap = {};
          const picksArray = picksResponse.picks || picksResponse;
          picksArray.forEach((pick) => {
            picksMap[pick.game.id] = pick;
          });

          setUserPicks(picksMap);
        }

        setStats(statsResponse);
        setUpcomingGames(gamesResponse);
      });
    } catch (err) {
      // Error handled by withLoading
    }
  }, [withLoading]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleMakePick = async (gameId, selectedTeam) => {
    try {
      const response = await apiService.post("/picks", {
        gameId,
        selectedTeam,
      });

      // Update the userPicks state
      setUserPicks((prev) => ({
        ...prev,
        [gameId]: response,
      }));
    } catch (err) {
      // Error handled by apiService
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Mobile view - swipeable picks deck (the same component /picks uses),
  // so picking/reviewing/editing feels identical everywhere on mobile.
  if (isMobile && upcomingGames.length > 0) {
    return (
      <div className="h-full">
        <CardBasedMobilePicks
          games={upcomingGames}
          userPicks={userPicks}
          onMakePick={handleMakePick}
          title={`Hey, ${user?.firstName || "there"}!`}
        />
      </div>
    );
  }

  // Mobile view - no games available
  if (isMobile) {
    return <EmptyState user={user} stats={stats} variant="mobile" />;
  }

  // Desktop view
  return (
    <>
      <h1 className="text-2xl font-bold mb-4">Welcome, {user?.firstName}!</h1>

      {/* Stats Cards */}
      <div className="mb-8">
        <StatsCards stats={stats} variant="desktop" />
      </div>

      {/* Upcoming Games */}
      {upcomingGames.length > 0 ? (
        <GamesList
          games={upcomingGames}
          userPicks={userPicks}
          onMakePick={handleMakePick}
          title="Games Next 48 Hours"
        />
      ) : (
        <EmptyState user={user} stats={stats} variant="desktop" />
      )}
    </>
  );
};

export default Dashboard;
